import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import DeleteIcon from "@material-ui/icons/Delete";
import VisibilityIcon from "@material-ui/icons/Visibility";

import souscriptionApi, { SouscriptionApiType } from "@/api/souscriptionApi";
import { FiltersPayloads, SortPayload } from "@/types/ApiResponse";
import { TypeBienEnum } from "@/types/TypeBien";
import { URLS } from "@/constants/urls";
import { formatDate } from "@/utils";
import { ApiTablePagine } from "@/components/ApiTablePagine";
import AfficheDevis from "@/views/PolicePno/SouscriptionsEnCours/AfficheDevis";
import SupprimeSouscription from "./SupprimeSouscription";

const PnoSouscriptionsEnCours = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const cleDeTriDateCreation = "dateCreation";

  const tableKey = useMemo(() => {
    return searchParams.toString();
  }, [searchParams]);

  useEffect(() => {
    /* Par défault, trier les date de création du plus récent au plus ancien */
    const filterParam = searchParams.get("filters");
    const sortParam = searchParams.get("sort");

    if (!filterParam && !sortParam) {
      setSearchParams({
        sort: `key:${cleDeTriDateCreation};direction:DESCENDING`,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSouscription = async (
    filters: FiltersPayloads,
    sort: SortPayload,
    currentPage: number,
    rowsPerPage: number
  ) => {
    return souscriptionApi.getAllSouscriptionsEnCoursPNO({
      page: currentPage,
      tri: sort,
      filtres: filters,
      size: rowsPerPage,
    });
  };

  const getAdresse = (e: SouscriptionApiType) =>
    e.adresse || e.ville || e.codePostal ? (
      <>
        {e.adresse} <br /> {e.ville} {e.codePostal}
      </>
    ) : (
      "-"
    );
  const getNomComplet = (e: SouscriptionApiType) =>
    e.nom && e.prenom ? e.nom + " " + e.prenom : "-";

  const getLabelTypeBien = (typeBien?: string) => {
    switch (typeBien) {
      case TypeBienEnum.MAISON:
        return t("pno.maison");
      case TypeBienEnum.APPARTEMENT:
        return t("pno.appartement");
      case TypeBienEnum.GARAGE:
        return t("pno.garage");
      case TypeBienEnum.LOCAL_COMMERCIAL:
        return t("pno.localCommercial");
      default:
        return "-";
    }
  };

  const SectionActions = ({
    souscription,
  }: {
    souscription: SouscriptionApiType;
  }) => {
    const redirect = useNavigate();

    return (
      <div style={{ display: "flex" }}>
        <VisibilityIcon
          style={{ fontSize: 18, margin: 5, cursor: "pointer" }}
          onClick={() =>
            redirect(URLS.PNO_SOUSCRIPTION + "/" + souscription.uuid)
          }
        />
        <SupprimeSouscription souscription={souscription}>
          <DeleteIcon style={{ fontSize: 18, margin: 5, cursor: "pointer" }} />
        </SupprimeSouscription>
      </div>
    );
  };

  return (
    <ApiTablePagine<SouscriptionApiType>
      key={tableKey} // Force le re-render
      getData={getSouscription}
      dataId="id"
      dataStructure={[
        {
          renderer: (e) =>
            formatDate(e.dateCreation.toString(), "DD/MM/YYYY") || "-",
          label: "Date de création",
          filter: { key: "dateCreation", rowType: "date" },
          sortKey: cleDeTriDateCreation,
          align: "left",
        },
        {
          renderer: (e) => e.numFoncia?.toString() || "-",
          label: "Référence propriétaire",
          filter: { key: "refProprio", rowType: "string", nullable: true },
        },
        {
          renderer: (e) => getNomComplet(e),
          label: "Nom complet",
          filter: [
            { label: "Nom", key: "nom", rowType: "string" },
            { label: "Prenom", key: "prenom", rowType: "string" },
          ],
          align: "left",
        },
        {
          renderer: (e) => e.numLot?.toString() || "",
          label: "Référence lot Foncia",
          filter: { key: "refLot", rowType: "string", nullable: true },
          align: "left",
        },
        {
          renderer: (e) => <div style={{ minWidth: 150 }}>{getAdresse(e)}</div>,
          label: t("dashboard.pno.contract.adresse"),
          filter: [
            { label: "Adresse", key: "adresse", rowType: "string" },
            { label: "Ville", key: "ville", rowType: "string" },
            { label: "Code Postal", key: "codePostal", rowType: "string" },
          ],
          align: "left",
        },
        {
          renderer: (e) => getLabelTypeBien(e.typeBien?.toString()),
          label: "Type de bien",
          align: "left",
        },
        {
          renderer: (e) => <AfficheDevis souscription={e} />,
          label: t("dashboard.pno.contract.devis"),
        },
        {
          renderer: (e) => <SectionActions souscription={e} />,
        },
      ]}
    />
  );
};

export default PnoSouscriptionsEnCours;
